import { CommonModule } from '@angular/common'
import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, ViewEncapsulation } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { RouterModule } from '@angular/router'
import { AnimationOptions } from 'ngx-lottie'
import { Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { CategoryService } from 'src/app/service/data/category.service'
import { TagService } from 'src/app/service/data/tag.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Themes } from '../../enums/enums'
import { DdcButtonSOneComponent } from '../ddc-button-s-one/ddc-button-s-one.component'
import { DdcDeleteDialogComponent } from '../ddc-delete-dialog/ddc-delete-dialog.component'

@Component({
  selector: 'ddc-edit-dialog',
  templateUrl: './ddc-edit-dialog.component.html',
  styleUrls: ['./ddc-edit-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    DdcButtonSOneComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcEditDialogComponent {
  _isLightTheme: boolean = true
  _isDarkTheme: boolean = false
  _isPaperTheme: boolean = false
  _theme: string
  private themeSubscription: Subscription

  _isEditBtnLoading = false
  _submitBtnText = 'Edit'
  _maxLength: number = 100
  _editError: string = ''
  _editedField: string = ''
  _deletedField: boolean = false

  _type: string = ''
  _element: any

  _form = new FormGroup({
    // ! Category
    _field: new FormControl('', [Validators.required, Validators.maxLength(this._maxLength)]),
  })

  // This is the option that uses the package's AnimationOption interface
  options: AnimationOptions = {
    path: '/assets/lottie/green-tick.json',
    loop: false, // Set loop to false for a single playthrough
    autoplay: true,
  }

  constructor(
    private logger: DdcLoggerService,
    public themeService: ThemeService,
    private cat_service: CategoryService,
    private tag_service: TagService,
    public dialog: MatDialog,
    private editDialogRef: MatDialogRef<DdcEditDialogComponent>,
    private deleteDialogRef: MatDialogRef<DdcDeleteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this._type = data.type
    this._element = data.element

    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._isLightTheme = theme === Themes.LIGHT
      this._isDarkTheme = theme === Themes.DARK
      this._isPaperTheme = theme === Themes.PAPER
    })
  }

  edit(): void {
    this.logger.info('DdcEditDialogComponent: edit()')

    if (!this._form.value._field) return

    this._isEditBtnLoading = true
    this._submitBtnText = 'Editing...'

    if (this._type == 'category') {
      this.cat_service.edit_cat(this.data.element).subscribe((res: any) => {
        if (res.success) {
          this._editError = ''
          this._editedField = this._form.value._field
        } else {
          this.logger.error('DdcEditDialogComponent: edit() | failed: res:', res)
          this._editError = res.res
          this._editedField = ''
        }
        this._isEditBtnLoading = false
        this._submitBtnText = 'Edit'
      })
    } else if (this._type == 'tag') {
      this.tag_service.edit_tag(this.data.element).subscribe((res: any) => {
        if (res.success) {
          this._editError = ''
          this._editedField = this._form.value._field
        } else {
          this.logger.error('DdcEditDialogComponent: edit() | failed: res:', res)
          this._editError = res.res
          this._editedField = ''
        }
        this._isEditBtnLoading = false
        this._submitBtnText = 'Edit'
      })
    }
  }

  closeDialog(): void {
    var target: String = ''
    if (this._type == 'category') target = 'category'
    else if (this._type == 'tag') target = 'tag'

    this.editDialogRef.close(target)
  }

  openDeleteDialog(): void {
    this.logger.info('DdcEditDialogComponent: openDeleteDialog()')

    const dialogRef = this.dialog.open(DdcDeleteDialogComponent, {
      data: {
        type: this._type,
        element: this._element,
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      // Handle any actions after the dialog is closed, e.g., updating the user's profile picture.
      if (result === 'delete-element') {
        if (this._type == 'category') {
          this.cat_service.delete_cat(this.data.element.id).subscribe((res: any) => {
            if (res.success) {
              this._editError = ''
              this._editedField = this._form.value._field
              this._deletedField = true
            } else {
              this.logger.error('DdcEditDialogComponent: openDeleteDialog() | failed: res:', res)
              this._editError = res.res
              this._editedField = ''
              this._deletedField = true
            }
            this._isEditBtnLoading = false
            this._submitBtnText = 'Edit'
          })
        } else if (this._type == 'tag') {
          this.tag_service.delete_tag(this.data.element.id).subscribe((res: any) => {
            if (res.success) {
              this._editError = ''
              this._editedField = this._form.value._field
              this._deletedField = true
            } else {
              this.logger.error('DdcEditDialogComponent: openDeleteDialog() | failed: res:', res)
              this._editError = res.res
              this._editedField = ''
              this._deletedField = true
            }
            this._isEditBtnLoading = false
            this._submitBtnText = 'Edit'
          })
        }
      }
    })
  }

  get field(): any {
    return this._form.get('_field')
  }
}
